import type { UserConfiguration, SaveConfigurationRequest, DeleteConfigurationRequest } from '@/api/generated/models'
import type { LucideIcon } from 'lucide-react'
import {
  Trophy,
  UserPlus,
  ChartBar,
  LayoutGrid,
  Medal,
  ArrowLeftRight,
  Handshake,
  ListOrdered,
  Award,
  Users,
  Building2,
  DollarSign,
  MessageCircle,
  ClipboardList,
  Calendar,
  UserCog,
  Newspaper,
  List,
  Settings,
  UsersRound,
  Network,
  ClipboardCheck,
  Plus,
  Play,
  TriangleAlert,
  CalendarDays,
  Pause,
  Hand,
} from 'lucide-react'

export type { LucideIcon }
export {
  Plus,
  Play,
  TriangleAlert,
  CalendarDays,
  Pause,
  Hand,
  Settings,
  UserPlus,
  UserCog,
  ArrowLeftRight,
  ChartBar,
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CardType = 'league' | 'team' | 'commish'
export type RibbonPosition = 'header' | 'top' | 'bottom' | 'left' | 'right'
export type CardViewMode = 'full' | 'compact'

export interface PerCardConfig {
  primaryColor?: string
  secondaryColor?: string
  ribbonPosition?: RibbonPosition
  cardViewMode?: CardViewMode
  shown?: string[]
  available?: string[]
  /**
   * Which sub-cards (team IDs and 'commish') are shown inside a league card.
   * undefined = show all (default). [] = show none.
   */
  visibleSubcards?: string[]
}

export interface DashboardConfig {
  columns?: number
  leagueOrder?: number[]
  hiddenLeagues?: number[]
  /** Hide each team's W-L record + place (spoiler control). Persisted display
   *  preference; hidden by default when unset (not tied to the blur-scores
   *  setting — that governs the schedule page, not the dashboard). */
  hideStandings?: boolean
  /** Global: team link ids hot-linked inline on every team block; the rest go
   *  under "+More". Applies to all teams. Unset -> default team-link set. */
  teamLinksShown?: string[]
  /** Global: league link ids hot-linked inline on every league card header; the
   *  rest go under "+More". Applies to all leagues. Unset -> default league-link set. */
  leagueLinksShown?: string[]
  enableAdvancedOrg?: boolean
  leagueLinks?: Record<string, PerCardConfig>
  teamLinks?: Record<string, PerCardConfig>
  commish?: Record<string, PerCardConfig>
}

export interface LinkDef {
  id: string
  path: string
  icon: LucideIcon
  label: string
  isDefault?: boolean
  requiresForumId?: boolean
  requiresCapability?: string
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export const CARD_DEFAULTS: Record<CardType, Required<Pick<PerCardConfig, 'primaryColor' | 'secondaryColor' | 'ribbonPosition' | 'cardViewMode'>>> = {
  league:  { primaryColor: '#113a57', secondaryColor: '#FFFFFF', ribbonPosition: 'header', cardViewMode: 'compact' },
  team:    { primaryColor: '#677A57', secondaryColor: '#FFFFFF', ribbonPosition: 'left',   cardViewMode: 'full' },
  commish: { primaryColor: '#a19900', secondaryColor: '#FFFFFF', ribbonPosition: 'bottom', cardViewMode: 'full' },
}

// ---------------------------------------------------------------------------
// Palettes — from legacy Dashboard.js:66-101 (7 rows × 6 cols each)
// ---------------------------------------------------------------------------

export const PRIMARY_COLOR_PALETTE: readonly string[] = [
  '#000000', '#1a1a1a', '#2b2b2b', '#333333', '#404040', '#4d4d4d',
  '#5c5c5c', '#666666', '#737373', '#808080', '#8c8c8c', '#999999',
  '#8B0000', '#A52A2A', '#B22222', '#CD5C5C', '#DC143C', '#C41E3A',
  '#CC5500', '#D2691E', '#E65100', '#a19900', '#DAA520', '#F57F17',
  '#1B5E20', '#2E7D32', '#388E3C', '#677A57', '#228B22', '#355E3B',
  '#004D40', '#00695C', '#006064', '#113a57', '#1565C0', '#0D47A1',
  '#4A148C', '#6A1B9A', '#7B1FA2', '#4B0082', '#880E4F', '#AD1457',
] as const

export const SECONDARY_COLOR_PALETTE: readonly string[] = [
  '#FFFFFF', '#FCFCFC', '#F8F8F8', '#F5F5F5', '#F0F0F0', '#EBEBEB',
  '#E0E0E0', '#D5D5D5', '#CACACA', '#BFBFBF', '#B4B4B4', '#A9A9A9',
  '#FFF0F2', '#FFE4E8', '#FFDADD', '#FFD0D4', '#FFC5CA', '#FFBAC0',
  '#FFF6E8', '#FFEEDB', '#FFE6CE', '#FFFBDA', '#FFF8C5', '#FFF5B0',
  '#F4FFF4', '#EAFAEA', '#E0F5E0', '#D6F0D6', '#CCEBCC', '#C2E6C2',
  '#E8FFFF', '#E0FAFA', '#D8F5F5', '#EAF5FE', '#DCEEFE', '#CEE7FE',
  '#F8F4FF', '#F2ECFC', '#ECE4F9', '#FFF4F8', '#FFECF3', '#FFE4EE',
] as const

// ---------------------------------------------------------------------------
// Link tables — from legacy Dashboard.js:14-55
// Array order = default display order in the ribbon.
// `isDefault: true` → appears in Visible Links by default.
// ---------------------------------------------------------------------------

// The league name itself links to /league/{id}/view, so there is no separate
// "League" overview link here. Standings is the single in-band hot link;
// everything else (incl. Free Agents) lives in the "+ More" menu.
export const LEAGUE_LINKS: LinkDef[] = [
  { id: 'standings',    path: '/league/{id}/standings',     icon: ListOrdered,    label: 'Standings',    isDefault: true },
  { id: 'stats',        path: '/league/{id}/stats',         icon: ChartBar,       label: 'Stats' },
  { id: 'scoreboard',   path: '/league/{id}/scoreboard',    icon: LayoutGrid,     label: 'Scoreboard' },
  { id: 'playoffs',     path: '/league/{id}/playoffs',      icon: Trophy,         label: 'Playoffs' },
  { id: 'leaders',      path: '/league/{id}/seasonleaders', icon: Medal,          label: 'Leaders' },
  { id: 'transactions', path: '/league/{id}/transactions',  icon: ArrowLeftRight, label: 'Transactions', requiresCapability: 'hasTransactions' },
  { id: 'trades',       path: '/league/{id}/trades',        icon: Handshake,      label: 'Trades',       requiresCapability: 'hasTrades' },
  { id: 'draft',        path: '/league/{id}/draft',         icon: ListOrdered,    label: 'Draft',        requiresCapability: 'hasDraft' },
  { id: 'awards',       path: '/league/{id}/awards',        icon: Award,          label: 'Awards',       requiresCapability: 'hasAwards' },
  { id: 'players',      path: '/league/{id}/players',       icon: Users,          label: 'Players' },
  { id: 'stadiums',     path: '/league/{id}/stadiums',      icon: Building2,      label: 'Stadiums' },
  { id: 'financials',   path: '/league/{id}/financials',    icon: DollarSign,     label: 'Financials' },
  { id: 'forum',        path: 'https://forums.basehit.com/forumdisplay.php/{forumId}', icon: MessageCircle, label: 'Forum', requiresForumId: true },
]

// The team name itself links to /team/{id}/view, so there is no separate
// "Team" overview link here.
// Array order MIRRORS the team secondary-nav rail (#13, see
// reference_team_page_usage_ga): actions (Lineups·Roster·Trades/FA·Draft) then
// results (Schedule·Stats·News·Transactions). Manager has no nav pill (it lives
// on the lineups page) so it trails the set as a dashboard-only owner link.
export const TEAM_LINKS: LinkDef[] = [
  // Sends people into the lineup editing flow at step ① Defense, nested under
  // /lineups/* (#361 — URL now matches the "Lineups" section label).
  { id: 'lineups',      path: '/team/{id}/lineups/defense',  icon: ClipboardList,  label: 'Lineups',             isDefault: true },
  { id: 'roster',       path: '/team/{id}/roster',           icon: Users,          label: 'Roster' },
  { id: 'freeagents',   path: '/team/{id}/freeagents',       icon: UserPlus,       label: 'Free Agents',         requiresCapability: 'hasFreeAgents' },
  { id: 'trades',       path: '/team/{id}/trades',           icon: ArrowLeftRight, label: 'Trades',              requiresCapability: 'hasTransactions' },
  { id: 'draftlist',    path: '/team/{id}/draftlist',        icon: ListOrdered,    label: 'Draft List',          isDefault: true, requiresCapability: 'hasDraft' },
  { id: 'schedule',     path: '/team/{id}/schedule',         icon: Calendar,       label: 'Schedule',            isDefault: true },
  { id: 'stats',        path: '/team/{id}/stats',            icon: ChartBar,       label: 'Stats' },
  { id: 'news',         path: '/team/{id}/news',             icon: Newspaper,      label: 'News' },
  { id: 'transactions', path: '/team/{id}/transactions',     icon: List,           label: 'Transactions',        requiresCapability: 'hasTransactions' },
  { id: 'manager',      path: '/team/{id}/manager',          icon: UserCog,        label: 'Manager' },
]

export const COMMISH_LINKS: LinkDef[] = [
  { id: 'settings',      path: '/league/{id}/settings',           icon: Settings,       label: 'League Settings',  isDefault: true },
  { id: 'teamownership', path: '/league/{id}/teamownership',      icon: UsersRound,     label: 'Team Ownership',   isDefault: true },
  { id: 'draftreorder',  path: '/league/{id}/draftreorder',       icon: ListOrdered,    label: 'Reorder Draft',    isDefault: true, requiresCapability: 'hasDraft' },
  { id: 'structure',     path: '/league/{id}/structure',          icon: Network,        label: 'League Structure' },
  { id: 'lmcheck',       path: '/league/{id}/lowminorscheck',      icon: ClipboardCheck, label: 'Low Minors Check' },
]

// ---------------------------------------------------------------------------
// Parsing + serialization
// ---------------------------------------------------------------------------

/**
 * Find the User-level config row (entityType='user').
 * Prefers the row keyed by userId (production convention), then falls back to
 * entityId=null (legacy/test convention), then to any user-type row.
 */
function findUserConfig(configs: UserConfiguration[] | undefined, userId?: number): UserConfiguration | undefined {
  if (!configs) return undefined
  const userRows = configs.filter(c => c.userConfigurationEntityTypeId === 'user')
  return (
    (userId !== undefined ? userRows.find(c => c.entityId === userId) : undefined) ??
    userRows.find(c => c.entityId === null || c.entityId === undefined) ??
    userRows[0]
  )
}

/**
 * Parse the dashboard config from the user-config array returned by
 * /Config/All. Tolerates missing keys, malformed JSON, and both the wrapped
 * (`{ dashboard: {...} }`) and unwrapped legacy shape.
 */
export function parseDashboardConfig(configs: UserConfiguration[] | undefined, userId?: number): DashboardConfig {
  const userConfig = findUserConfig(configs, userId)
  if (!userConfig?.configurationValue) return {}
  try {
    const parsed = JSON.parse(userConfig.configurationValue)
    const dashboard = parsed?.dashboard ?? parsed
    if (typeof dashboard !== 'object' || dashboard === null) return {}
    return {
      columns: typeof dashboard.columns === 'number' ? dashboard.columns : undefined,
      leagueOrder: Array.isArray(dashboard.leagueOrder) ? dashboard.leagueOrder : undefined,
      hiddenLeagues: Array.isArray(dashboard.hiddenLeagues) ? dashboard.hiddenLeagues : undefined,
      hideStandings: typeof dashboard.hideStandings === 'boolean' ? dashboard.hideStandings : undefined,
      teamLinksShown: Array.isArray(dashboard.teamLinksShown) ? dashboard.teamLinksShown : undefined,
      leagueLinksShown: Array.isArray(dashboard.leagueLinksShown) ? dashboard.leagueLinksShown : undefined,
      enableAdvancedOrg: typeof dashboard.enableAdvancedOrg === 'boolean' ? dashboard.enableAdvancedOrg : undefined,
      leagueLinks: dashboard.leagueLinks ?? undefined,
      teamLinks: dashboard.teamLinks ?? undefined,
      commish: dashboard.commish ?? undefined,
    }
  } catch {
    return {}
  }
}

/**
 * Build the JSON string to send as `configurationValue` on a PUT /Config.
 * Preserves any other top-level keys in the existing envelope (so settings
 * stored under non-"dashboard" keys are not clobbered).
 */
export function serializeDashboardEnvelope(existingValue: string | null | undefined, nextDashboard: DashboardConfig): string {
  let envelope: Record<string, unknown> = {}
  if (existingValue) {
    try {
      const parsed = JSON.parse(existingValue)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && 'dashboard' in parsed) {
        // Existing envelope had a "dashboard" key — preserve other siblings.
        envelope = parsed as Record<string, unknown>
      }
      // If existing was un-wrapped legacy shape, ignore it: we always write wrapped now.
    } catch {
      // malformed — start fresh
    }
  }
  envelope.dashboard = nextDashboard
  return JSON.stringify(envelope)
}

/** Pull the raw configurationValue string off the user-level row, for re-serialization. */
export function getRawUserConfigValue(configs: UserConfiguration[] | undefined, userId?: number): string | null {
  return findUserConfig(configs, userId)?.configurationValue ?? null
}

/**
 * Build the PUT /Config request body for saving dashboard config.
 * Always targets entityId = userId (production convention).
 */
export function buildDashboardSaveRequest(
  userId: number,
  existingValue: string | null,
  nextDashboard: DashboardConfig,
): SaveConfigurationRequest {
  return {
    userId,
    entityType: 'user',
    entityId: userId,
    configurationValue: serializeDashboardEnvelope(existingValue, nextDashboard),
  }
}

/**
 * Build the DELETE /Config request body for resetting dashboard config.
 * Always targets entityId = userId (production convention).
 */
export function buildDashboardResetRequest(userId: number): DeleteConfigurationRequest {
  return { userId, entityType: 'user', entityId: userId }
}
